import React, { useEffect, useState } from "react";
import { Plus, Loader2 } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Panel, DataTable, Fld, money, fmtDate, inputCls } from "./shared";
import { ReportDownloads } from "./FinanceReports";

const METHODS = ["Cash", "Check", "Bank Transfer", "Online"];
const today = () => new Date().toISOString().slice(0, 10);
const EMPTY = { shipment_id: "", amount: "", date: today(), method: "Cash", reference: "", notes: "", deduction_rate: 0 };
const RATES = [0, 1, 2, 5, 10];

const COLUMNS = [
  { key: "receipt_number", label: "Receipt #", mono: true },
  { key: "invoice_number", label: "Invoice #", mono: true },
  { key: "shipment_number", label: "Shipment #", mono: true },
  { key: "client_name", label: "Client" },
  { key: "date", label: "Date", render: (r) => fmtDate(r.date) },
  { key: "method", label: "Method" },
  { key: "reference", label: "Reference", mono: true },
  { key: "amount", label: "Applied", right: true, render: (r) => money(r.amount) },
  { key: "deduction_rate", label: "Deduction", right: true, render: (r) => (r.deduction_rate ? `${r.deduction_rate}% · ${money(r.withheld)}` : "—") },
  { key: "net_received", label: "Net received", right: true, render: (r) => money(r.net_received ?? r.amount) },
  { key: "recorded_by_name", label: "Recorded by" },
];

export default function CollectionsTab({ onChanged }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [c, i] = await Promise.all([api.get("/finance/collections"), api.get("/finance/invoices")]);
      setRows(c.data); setOpen(i.data.filter((x) => x.outstanding > 0));
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); }, []);

  const pick = (id) => setF({ ...f, shipment_id: id, amount: open.find((x) => x.shipment_id === id)?.outstanding ?? "" });
  const inv = open.find((x) => x.shipment_id === f.shipment_id);
  const base = (parseFloat(f.amount) || 0) * (inv?.vatable_ratio ?? 1);
  const withheld = Math.round(base * f.deduction_rate) / 100;
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/finance/collections", { ...f, amount: parseFloat(f.amount) || 0 });
      toast.success(`${data.receipt_number} recorded · invoice ${data.invoice_status}`);
      setF(EMPTY); load(); onChanged();
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6">
      <Panel title="Record a collection" subtitle="The invoice status updates to Paid, Partial or Outstanding automatically.">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Fld label="Invoice" className="sm:col-span-2 lg:col-span-1">
            <Select value={f.shipment_id} onValueChange={pick}>
              <SelectTrigger data-testid="select-collection-invoice" className={inputCls}><SelectValue placeholder="Choose an open invoice" /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                {open.map((x) => <SelectItem key={x.shipment_id} value={x.shipment_id}>{x.invoice_number} · {x.shipment_number} · {x.client_name} · {money(x.outstanding)} due</SelectItem>)}
              </SelectContent>
            </Select>
          </Fld>
          <Fld label="Amount"><Input data-testid="input-collection-amount" type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className={`${inputCls} font-mono-ops`} /></Fld>
          <Fld label="Date received"><Input data-testid="input-collection-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className={inputCls} /></Fld>
          <Fld label="Method">
            <Select value={f.method} onValueChange={(v) => setF({ ...f, method: v })}>
              <SelectTrigger data-testid="select-collection-method" className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">{METHODS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
            </Select>
          </Fld>
          <Fld label="Reference (OR / check / transfer #)"><Input data-testid="input-collection-reference" value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} className={`${inputCls} font-mono-ops`} /></Fld>
          <Fld label="Notes"><Input data-testid="input-collection-notes" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className={inputCls} /></Fld>
          <Fld label="Deduction (withholding on VATable amount)">
            <Select value={String(f.deduction_rate)} onValueChange={(v) => setF({ ...f, deduction_rate: Number(v) })}>
              <SelectTrigger data-testid="select-collection-deduction" className={inputCls}><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">{RATES.map((r) => <SelectItem key={r} value={String(r)}>{r ? `${r}% deduction` : "No deduction"}</SelectItem>)}</SelectContent>
            </Select>
          </Fld>
        </div>
        <div data-testid="collection-breakdown" className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 font-mono-ops text-sm">
          {[["Applied to invoice", parseFloat(f.amount) || 0, "col-applied"], ["VATable amount", base, "col-base"], [`Less ${f.deduction_rate}%`, withheld, "col-withheld"], ["Net collected", (parseFloat(f.amount) || 0) - withheld, "col-net"]].map(([lb, v, t]) => (
            <div key={t} className="bg-slate-950/60 border border-slate-800 rounded-md p-3"><div className="text-[10px] uppercase tracking-widest text-slate-500 font-sans">{lb}</div><div data-testid={t} className="text-slate-100">{money(v)}</div></div>
          ))}
        </div>
        <Button data-testid="button-save-collection" onClick={save} disabled={busy || !f.shipment_id} className="mt-4 bg-blue-600 hover:bg-blue-500">
          {busy ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Plus size={14} className="mr-2" />} Record collection
        </Button>
      </Panel>
      <Panel title="Collections history" right={<ReportDownloads report="collections" />}>
        <DataTable testid="collections-table" columns={COLUMNS} rows={rows} empty="No collections recorded yet." />
      </Panel>
    </div>
  );
}
